import React from "react";
import { Box } from "@mui/material";
import PropTypes from "prop-types";

function slideDirection(fromVisual, emptyVisual, gridSize) {
    const sameRow = Math.floor(fromVisual / gridSize) === Math.floor(emptyVisual / gridSize);
    if (fromVisual === emptyVisual - 1 && sameRow) return "right";
    if (fromVisual === emptyVisual + 1 && sameRow) return "left";
    if (fromVisual === emptyVisual - gridSize) return "down";
    if (fromVisual === emptyVisual + gridSize) return "up";
    return null;
}

/** 4×4 scramble with the hole one cell in from the top-left, so all four arrows show. */
const FALLBACK_SCRAMBLE_4 = [2, 7, 1, 4, 3, 15, 8, 0, 12, 9, 5, 11, 6, 10, 14, 13];

function withInteriorHole(cells, gridSize) {
    const next = cells.slice();
    const emptySlice = gridSize * gridSize - 1;
    const emptyVisual = next.findIndex((value) => value === emptySlice);
    if (emptyVisual < 0) return next;
    const row = Math.floor(emptyVisual / gridSize);
    const col = emptyVisual % gridSize;
    if (row > 0 && row < gridSize - 1 && col > 0 && col < gridSize - 1) return next;
    const target = gridSize + 1;
    const held = next[target];
    next[target] = next[emptyVisual];
    next[emptyVisual] = held;
    return next;
}

function DirectionArrow({ direction }) {
    const rotate = { up: 0, right: 90, down: 180, left: 270 }[direction] ?? 0;
    const edge = "10%";
    const pos =
        direction === "right"
            ? { right: edge, top: "50%", transform: `translateY(-50%) rotate(${rotate}deg)` }
            : direction === "left"
              ? { left: edge, top: "50%", transform: `translateY(-50%) rotate(${rotate}deg)` }
              : direction === "down"
                ? { bottom: edge, left: "50%", transform: `translateX(-50%) rotate(${rotate}deg)` }
                : { top: edge, left: "50%", transform: `translateX(-50%) rotate(${rotate}deg)` };
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                width: 0,
                height: 0,
                borderLeft: "0.275em solid transparent",
                borderRight: "0.275em solid transparent",
                borderBottom: "0.475em solid #fff",
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.85))",
                zIndex: 2,
                ...pos,
            }}
        />
    );
}

DirectionArrow.propTypes = {
    direction: PropTypes.oneOf(["up", "down", "left", "right"]).isRequired,
};

export default function ScreenshotScrambledBoard({ imageUrl, gridSize, startingLayout }) {
    const gs = Number(gridSize) > 1 ? Number(gridSize) : 4;
    const n = gs * gs;
    const solved = Array.from({ length: n }, (_, index) => index);
    const source =
        Array.isArray(startingLayout) && startingLayout.length === n
            ? startingLayout.map((value) => Number(value))
            : gs === 4
              ? FALLBACK_SCRAMBLE_4
              : solved;
    const looksSolved = source.every((value, index) => value === index);
    const cells = withInteriorHole(looksSolved && gs === 4 ? FALLBACK_SCRAMBLE_4 : source, gs);
    const emptySlice = n - 1;
    const emptyVisual = cells.findIndex((value) => value === emptySlice);

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                aspectRatio: "1 / 1",
                boxSizing: "border-box",
                display: "grid",
                gridTemplateColumns: `repeat(${gs}, minmax(0, 1fr))`,
                gap: "2.4%",
                p: "2.4%",
                bgcolor: "#1a2332",
                borderRadius: "12px",
            }}
        >
            {cells.map((slice, visual) => {
                const empty = slice === emptySlice;
                const row = Math.floor(slice / gs);
                const col = slice % gs;
                const direction = !empty && emptyVisual >= 0 ? slideDirection(visual, emptyVisual, gs) : null;
                return (
                    <Box
                        key={`scramble-cell-${visual}`}
                        sx={{
                            position: "relative",
                            minWidth: 0,
                            minHeight: 0,
                            overflow: "hidden",
                            borderRadius: "4px",
                            bgcolor: empty ? "rgba(0, 0, 0, 0.55)" : "transparent",
                            containerType: "size",
                            fontSize: "clamp(16px, 28cqi, 48px)",
                        }}
                    >
                        {empty || !imageUrl ? null : (
                            <Box
                                component="img"
                                src={imageUrl}
                                alt=""
                                draggable={false}
                                sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    width: `${gs * 100}%`,
                                    height: `${gs * 100}%`,
                                    maxWidth: "none",
                                    objectFit: "fill",
                                    transform: `translate(-${(col * 100) / gs}%, -${(row * 100) / gs}%)`,
                                    transformOrigin: "top left",
                                    pointerEvents: "none",
                                }}
                            />
                        )}
                        {direction ? <DirectionArrow direction={direction} /> : null}
                    </Box>
                );
            })}
        </Box>
    );
}

ScreenshotScrambledBoard.propTypes = {
    imageUrl: PropTypes.string,
    gridSize: PropTypes.number,
    startingLayout: PropTypes.arrayOf(PropTypes.number),
};
